import type { StyleSpecification } from 'maplibre-gl';

export interface AwsMapCredentials {
  awsMapApiKey: string;
  awsMapRegion: string;
}

const EMPTY_STYLE: StyleSpecification = {
  version: 8,
  sources: {},
  layers: [],
};

/**
 * AWS Location Service's v2 Maps API is resource-less (no named "map"
 * to create) — it's authorized against the shared `provider/default`
 * ARN and serves one of a fixed set of built-in styles by name.
 * https://docs.aws.amazon.com/location/latest/developerguide/using-apikeys.html
 */
const MAP_STYLE = 'Standard';
const AWS_MAP_HOST_PATTERN = /^maps\.geo\.[a-z0-9-]+\.amazonaws\.com$/i;

function getAwsRegion(regionOrArn: string): string {
  const arnRegion = /^arn:[^:]+:geo:([^:]+):/.exec(regionOrArn)?.[1];

  return arnRegion ?? regionOrArn;
}

export function addAwsMapApiKey(requestUrl: string, apiKey: string): string {
  if (!apiKey) {
    return requestUrl;
  }

  try {
    if (!AWS_MAP_HOST_PATTERN.test(new URL(requestUrl).hostname)) {
      return requestUrl;
    }
  } catch {
    return requestUrl;
  }

  const encodedApiKey = encodeURIComponent(apiKey);

  if (/[?&]key=[^&]*/.test(requestUrl)) {
    return requestUrl.replace(
      /([?&])key=[^&]*/,
      (_match, separator: string) => `${separator}key=${encodedApiKey}`,
    );
  }

  const separator = requestUrl.includes('?') ? '&' : '?';

  return `${requestUrl}${separator}key=${encodedApiKey}`;
}

/**
 * Falls back to a minimal valid style (no tiles) when the AWS Location
 * Service credentials haven't been provisioned yet, so the map still
 * builds/renders markers and clusters without them.
 */
export function buildAwsMapStyle(
  credentials: AwsMapCredentials,
): StyleSpecification | string {
  const { awsMapApiKey, awsMapRegion } = credentials;

  if (!awsMapApiKey || !awsMapRegion) {
    return EMPTY_STYLE;
  }

  const region = getAwsRegion(awsMapRegion);

  return `https://maps.geo.${region}.amazonaws.com/v2/styles/${MAP_STYLE}/descriptor?key=${encodeURIComponent(awsMapApiKey)}`;
}
